import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle, ArrowRight, Check, Clock, Eye, EyeOff, Info, KeyRound, Loader2, Lock, Mail, ShieldCheck,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { errorText } from '../services/api';

/* Super-admin sign-in. Two steps that the server binds together: a correct password returns a 5-minute
   challenge, and the PIN is only accepted with that challenge (see backend-admin/core/security.py). */

const CHALLENGE_SECONDS = 5 * 60;
const isLocal = ['localhost', '127.0.0.1'].includes(window.location.hostname);

type Step = 'password' | 'pin';

function Stepper({ step }: { step: Step }) {
  const item = (n: number, label: string, state: 'done' | 'active' | 'todo') => (
    <div className="flex items-center gap-2">
      <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
        state === 'done' ? 'bg-emerald-600 text-white' : state === 'active' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-400'}`}>
        {state === 'done' ? <Check className="h-3.5 w-3.5" /> : n}
      </span>
      <span className={`text-xs font-medium ${state === 'todo' ? 'text-slate-400' : 'text-slate-700'}`}>{label}</span>
    </div>
  );
  return (
    <div className="flex items-center gap-3">
      {item(1, 'Password', step === 'pin' ? 'done' : 'active')}
      <span className={`h-px flex-1 ${step === 'pin' ? 'bg-emerald-300' : 'bg-slate-200'}`} />
      {item(2, 'PIN', step === 'pin' ? 'active' : 'todo')}
    </div>
  );
}

function Notice({ tone, icon: Icon, children }: { tone: 'red' | 'amber' | 'blue'; icon: React.ElementType; children: React.ReactNode }) {
  const cls = { red: 'border-red-200 bg-red-50 text-red-700', amber: 'border-amber-200 bg-amber-50 text-amber-800', blue: 'border-blue-100 bg-blue-50 text-blue-800' }[tone];
  return (
    <div className={`flex items-start gap-2 rounded-lg border px-3 py-2.5 text-sm ${cls}`} role="alert">
      <Icon className="mt-0.5 h-4 w-4 shrink-0" /><span>{children}</span>
    </div>
  );
}

export default function Login() {
  const { isAuthenticated, checkPassword, verifyPin } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [step, setStep] = useState<Step>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [challenge, setChallenge] = useState('');
  const [name, setName] = useState('');
  const [digits, setDigits] = useState<string[]>(Array(6).fill(''));
  const [secondsLeft, setSecondsLeft] = useState(CHALLENGE_SECONDS);
  const [error, setError] = useState<string | null>(null);
  const [locked, setLocked] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(params.get('expired') ? 'Your session ended. Sign in again to continue.' : null);
  const [busy, setBusy] = useState(false);
  const pinRefs = useRef<(HTMLInputElement | null)[]>([]);

  // The PIN window: the server's challenge expires after 5 minutes, so show it and send the user back when it does.
  useEffect(() => {
    if (step !== 'pin') return;
    setSecondsLeft(CHALLENGE_SECONDS);
    pinRefs.current[0]?.focus();
    const started = Date.now();
    const id = window.setInterval(() => {
      const left = CHALLENGE_SECONDS - Math.floor((Date.now() - started) / 1000);
      setSecondsLeft(Math.max(0, left));
      if (left <= 0) {
        window.clearInterval(id);
        backToPassword('Your PIN window closed after 5 minutes. Enter your password again.');
      }
    }, 1000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  if (isAuthenticated) return <Navigate to="/" replace />;

  function backToPassword(message?: string) {
    setStep('password'); setChallenge(''); setDigits(Array(6).fill(''));
    setError(message || null);
  }

  const handleFailure = (err: any, fallback: string) => {
    const msg = errorText(err, fallback);
    if (err?.response?.status === 429) { setLocked(msg); setError(null); return msg; }
    setError(msg);
    return msg;
  };

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) { setError('Enter your email'); return; }
    if (!password) { setError('Enter your password'); return; }
    setBusy(true); setError(null); setInfo(null); setLocked(null);
    try {
      const res = await checkPassword(email.trim(), password);
      setChallenge(res.challenge); setName(res.name); setPassword(''); setShowPassword(false); setStep('pin');
    } catch (err) {
      handleFailure(err, "Couldn't sign in. Try again.");
    } finally { setBusy(false); }
  };

  const submitPin = async (pin: string) => {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      await verifyPin(challenge, pin);
      navigate('/', { replace: true });
    } catch (err: any) {
      const msg = handleFailure(err, "Couldn't verify the PIN.");
      setDigits(Array(6).fill(''));
      if (err?.response?.status === 401 && /timed out/i.test(msg)) backToPassword(msg);
      else pinRefs.current[0]?.focus();
    } finally { setBusy(false); }
  };

  const setDigit = (i: number, raw: string) => {
    const clean = raw.replace(/\D/g, '');
    if (clean.length > 1) {                       // pasted or autofilled the whole PIN
      const next = clean.slice(0, 6).split('').concat(Array(6).fill('')).slice(0, 6);
      setDigits(next);
      if (next.every(Boolean)) submitPin(next.join('')); else pinRefs.current[Math.min(clean.length, 5)]?.focus();
      return;
    }
    const next = [...digits]; next[i] = clean; setDigits(next);
    if (error) setError(null);
    if (clean && i < 5) pinRefs.current[i + 1]?.focus();
    if (next.every(Boolean)) submitPin(next.join(''));
  };

  const onPinKey = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) pinRefs.current[i - 1]?.focus();
    if (e.key === 'ArrowLeft' && i > 0) pinRefs.current[i - 1]?.focus();
    if (e.key === 'ArrowRight' && i < 5) pinRefs.current[i + 1]?.focus();
  };

  const mm = Math.floor(secondsLeft / 60), ss = String(secondsLeft % 60).padStart(2, '0');
  const inputCls = 'w-full rounded-lg border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10 disabled:bg-slate-50';

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <header className="flex items-center justify-end px-6 py-4">
        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${isLocal ? 'bg-amber-50 text-amber-800' : 'bg-red-50 text-red-700'}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${isLocal ? 'bg-amber-500' : 'bg-red-500'}`} />
          {isLocal ? 'Development environment' : 'Production'}
        </span>
      </header>

      <main className="flex flex-1 items-center justify-center px-4 pb-10">
        <div className="w-full max-w-[400px]">
          <div className="mb-7 flex flex-col items-center gap-3">
            <img src="/logo.png" alt="FluxWatch" className="h-14 w-auto" />
            <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600">
              <ShieldCheck className="h-3.5 w-3.5 text-red-600" /> Admin console
            </span>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
            <Stepper step={step} />

            {step === 'password' ? (
              <form onSubmit={submitPassword} className="mt-6 space-y-4" noValidate>
                <div>
                  <h1 className="text-lg font-semibold text-slate-900">Sign in</h1>
                  <p className="mt-1 text-sm text-slate-500">For the FluxWatch platform team only.</p>
                </div>

                {info && <Notice tone="blue" icon={Info}>{info}</Notice>}
                {locked && <Notice tone="red" icon={Lock}>{locked}</Notice>}

                <label className="block">
                  <span className="mb-1.5 block text-xs font-medium text-slate-600">Work email</span>
                  <div className="relative">
                    <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input type="email" autoComplete="username" autoFocus value={email} disabled={busy}
                      onChange={e => { setEmail(e.target.value); setError(null); }} placeholder="you@fluxvell.com" className={inputCls} />
                  </div>
                </label>

                <label className="block">
                  <span className="mb-1.5 block text-xs font-medium text-slate-600">Password</span>
                  <div className="relative">
                    <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input type={showPassword ? 'text' : 'password'} autoComplete="current-password" value={password} disabled={busy}
                      onChange={e => { setPassword(e.target.value); setError(null); }}
                      onKeyUp={e => setCapsLock(e.getModifierState('CapsLock'))}
                      onKeyDown={e => setCapsLock(e.getModifierState('CapsLock'))}
                      className={`${inputCls} pr-10`} />
                    <button type="button" onClick={() => setShowPassword(v => !v)} tabIndex={-1}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:text-slate-700">
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {capsLock && <span className="mt-1.5 flex items-center gap-1 text-xs text-amber-700"><AlertTriangle className="h-3.5 w-3.5" />Caps Lock is on</span>}
                </label>

                {error && <Notice tone="red" icon={AlertTriangle}>{error}</Notice>}

                <button type="submit" disabled={busy}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Continue <ArrowRight className="h-4 w-4" /></>}
                </button>
              </form>
            ) : (
              <div className="mt-6">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h1 className="text-lg font-semibold text-slate-900">Enter your PIN</h1>
                    <p className="mt-1 text-sm text-slate-500">
                      Signing in as <span className="font-medium text-slate-700">{name || email}</span>
                    </p>
                  </div>
                  <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-700"><KeyRound className="h-4 w-4" /></span>
                </div>

                <div className="mt-5 flex justify-between gap-2" onPaste={e => { e.preventDefault(); setDigit(0, e.clipboardData.getData('text')); }}>
                  {digits.map((d, i) => (
                    <input key={i} ref={el => { pinRefs.current[i] = el; }} value={d} type="password" inputMode="numeric"
                      autoComplete={i === 0 ? 'one-time-code' : 'off'} maxLength={6} disabled={busy || !!locked}
                      aria-label={`PIN digit ${i + 1} of 6`}
                      onChange={e => setDigit(i, e.target.value)} onKeyDown={e => onPinKey(i, e)} onFocus={e => e.target.select()}
                      className={`h-12 w-full min-w-0 rounded-lg border text-center text-lg font-semibold text-slate-900 focus:outline-none focus:ring-2 disabled:bg-slate-50 ${
                        error ? 'border-red-300 focus:border-red-500 focus:ring-red-100' : d ? 'border-slate-900 focus:ring-slate-900/10' : 'border-slate-200 focus:border-slate-900 focus:ring-slate-900/10'}`} />
                  ))}
                </div>

                <div className="mt-4 space-y-3">
                  {locked && <Notice tone="red" icon={Lock}>{locked}</Notice>}
                  {error && <Notice tone="red" icon={AlertTriangle}>{error}</Notice>}
                  {busy && <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Checking your PIN…</div>}
                </div>

                <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-4 text-xs">
                  <span className={`flex items-center gap-1.5 ${secondsLeft <= 60 ? 'text-amber-700' : 'text-slate-500'}`}>
                    <Clock className="h-3.5 w-3.5" /> Window closes in {mm}:{ss}
                  </span>
                  <button onClick={() => backToPassword()} className="font-medium text-slate-600 hover:text-slate-900">Use a different account</button>
                </div>
              </div>
            )}
          </div>

          <p className="mt-5 flex items-center justify-center gap-1.5 text-center text-xs text-slate-500">
            <Lock className="h-3.5 w-3.5 shrink-0" /> Every attempt is logged · 5 failed tries locks the account for 15 min
          </p>
        </div>
      </main>

      <footer className="pb-6 text-center text-xs text-slate-400">© {new Date().getFullYear()} Fluxvell · FluxWatch platform administration</footer>
    </div>
  );
}
